import React, { useState, useEffect } from "react";
import { Modal } from "@themesberg/react-bootstrap";
import CustomBarChart from "./NewCharts/CustomBarChart";
import PieChartComponent from "./NewCharts/CustomPieChart";
import { data2, data3, data4, pieChartData } from "./data";
import moment from "moment";
import { fetchApi } from "../../redux/api";
import { formatNumber } from "../../redux/utils";
import { useResponsive } from "../../hooks/useResponsive";
import "./arrow.css";

export const getAllData = (query_type, date_from, date_to, cb) => {
  fetchApi(
    `kifmis/dashboard-budget?query_type=${query_type}&from=${date_from}&to=${date_to}`
  )
    .then((data) => cb(data.results))
    .catch((err) => console.log(err));
};

const getCurrentQuarter = () => {
  const month = new Date().getMonth() + 1;
  if (month <= 3)  return "Q1";
  if (month <= 6)  return "Q2";
  if (month <= 9)  return "Q3";
  return "Q4";
};

const getQuarterDateRange = (quarter) => {
  const year = new Date().getFullYear();
  const ranges = {
    Q1: { from: `${year}-01-01`, to: `${year}-03-31` },
    Q2: { from: `${year}-04-01`, to: `${year}-06-30` },
    Q3: { from: `${year}-07-01`, to: `${year}-09-30` },
    Q4: { from: `${year}-10-01`, to: `${year}-12-31` },
  };
  return ranges[quarter] || { from: "", to: "" };
};

/* ── Reusable sub-components ─────────────────────────────────────── */

const StatCard = ({ label, value, color = "#0d6efd", icon, subtitle }) => (
  <div style={{
    background: "#fff", borderRadius: 12, padding: "16px 18px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)", borderLeft: `4px solid ${color}`,
    minWidth: 0,          /* prevents overflow in grid */
  }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 11, color: "#6c757d", fontWeight: 600,
          textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: 4 }}>
          {label}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#212529",
          lineHeight: 1.2, wordBreak: "break-word" }}>
          ₦{formatNumber(value || 0)}
        </div>
        {subtitle && (
          <div style={{ fontSize: 11, color: "#6c757d", marginTop: 3 }}>{subtitle}</div>
        )}
      </div>
      {icon && (
        <div style={{
          width: 36, height: 36, borderRadius: 9, background: `${color}18`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 16, color, flexShrink: 0, marginLeft: 8,
        }}>
          {icon}
        </div>
      )}
    </div>
  </div>
);

const ChartCard = ({ title, children, onExpand }) => (
  <div style={{
    background: "#fff", borderRadius: 12, padding: "14px 16px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  }}>
    <div style={{ display: "flex", justifyContent: "space-between",
      alignItems: "center", marginBottom: 10, gap: 8 }}>
      <h6 style={{ fontSize: 12, fontWeight: 600, color: "#343a40",
        margin: 0, lineHeight: 1.3, flex: 1 }}>
        {title}
      </h6>
      {onExpand && (
        <button onClick={onExpand} style={{
          background: "none", border: "1px solid #dee2e6", borderRadius: 6,
          padding: "2px 10px", fontSize: 11, color: "#0d6efd",
          cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
        }}>
          Expand
        </button>
      )}
    </div>
    {children}
  </div>
);

const FlowCard = ({ revenueTotal, expenseTotal, quarter, year }) => {
  const netFlow     = (revenueTotal || 0) - (expenseTotal || 0);
  const netPositive = netFlow >= 0;

  const row = (bg, border, labelColor, label, amount, icon) => (
    <div style={{
      background: bg, borderRadius: 10, padding: "12px 14px",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      borderLeft: `4px solid ${border}`,
    }}>
      <div>
        <div style={{ fontSize: 10, color: labelColor, fontWeight: 700,
          textTransform: "uppercase", marginBottom: 2 }}>
          {label}
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: labelColor }}>
          {amount}
        </div>
      </div>
      <div style={{ fontSize: 22, color: border }}>{icon}</div>
    </div>
  );

  return (
    <div style={{
      background: "#fff", borderRadius: 12, padding: "16px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.08)", height: "100%",
    }}>
      <h6 style={{ fontSize: 12, fontWeight: 600, color: "#343a40", marginBottom: 14 }}>
        {year} {quarter} — Flow of Funds
      </h6>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {row("#e8f5e9", "#2e7d32", "#1b5e20", "Incoming (Revenue)",
          `₦${formatNumber(revenueTotal || 0)}`, "↑")}
        {row("#fce4ec", "#c62828", "#b71c1c", "Outgoing (Expenditure)",
          `₦${formatNumber(expenseTotal || 0)}`, "↓")}
        {row(
          netPositive ? "#e3f2fd" : "#fff3e0",
          netPositive ? "#1565c0" : "#e65100",
          netPositive ? "#0d47a1" : "#bf360c",
          "Net Balance",
          `${netPositive ? "+" : ""}₦${formatNumber(Math.abs(netFlow))}`,
          netPositive ? "✓" : "!"
        )}
      </div>
    </div>
  );
};

/* ── Main dashboard ──────────────────────────────────────────────── */

function NewDashboard() {
  const { isMobile } = useResponsive();
  const [modalOpen, setModalOpen]         = useState(false);
  const [selectedChart, setSelectedChart] = useState(null);

  const today   = moment().format("YYYY-MM-DD");
  const yearAgo = moment().subtract(1, "year").format("YYYY-MM-DD");
  const year    = moment().format("YYYY");

  const [selectQuarter, setSelectQuarter] = useState({
    quarter: getCurrentQuarter(), from: yearAgo, to: today,
  });

  const [budgeted,         setBudgeted]         = useState([]);
  const [expense,          setExpense]           = useState([]);
  const [revenue,          setRevenue]           = useState([]);
  const [budgetActual,     setBudgetActual]      = useState([]);
  const [revenueByEconomic, setRevenueByEconomic] = useState([]);
  const [expByEconomic,    setExpByEconomic]     = useState([]);
  const [mainSector,       setMainSector]        = useState([]);
  const [expByFunc,        setExpByFunc]         = useState([]);

  useEffect(() => {
    const { from, to } = selectQuarter;
    getAllData("budget_amount",    from, to, setBudgeted);
    getAllData("expenses",         from, to, setExpense);
    getAllData("revenue",          from, to, setRevenue);
    getAllData("exp_by_func",      from, to, setExpByFunc);
    getAllData("revenue_economic", from, to, setRevenueByEconomic);
    getAllData("exp_economic",     from, to, setExpByEconomic);
    getAllData("exp_main_sector",  from, to, setMainSector);
    getAllData("budget-actual",    from, to, (resp) => {
      const r = resp?.[0] || {};
      const ratio = parseFloat(r["Total Actual"] || 0) / parseFloat(r["Total Budget"] || 1);
      setBudgetActual([
        { name: "REVENUE",     "Total Budget": r["Total Budget"], "Total Actual": r["Total Actual"], Performance: ratio * 100 },
        { name: "EXPENDITURE", "Total Budget": r["Total Budget"], "Total Actual": r["Total Actual"], Performance: ratio * 100 },
      ]);
    });
  }, [selectQuarter.from, selectQuarter.to]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "quarter" && value !== "custom") {
      setSelectQuarter({ quarter: value, ...getQuarterDateRange(value) });
    } else {
      setSelectQuarter((p) => ({ ...p, [name]: value }));
    }
  };

  const openModal = (title, chart) => {
    setSelectedChart({ title, chart });
    setModalOpen(true);
  };

  const newRev = (revenueByEconomic || []).map((item) => ({
    ...item,
    name:               item.description,
    "Quarterly Budget": item.approve_amount,
    "Actual for Quarter": item.actual_amount,
    Performance: 30,
  }));

  const revenueTotal = revenue?.[0]?.total  || 0;
  const expenseTotal = expense?.[0]?.total  || 0;
  const budgetTotal  = budgeted?.[0]?.total || budgetActual?.[0]?.["Total Budget"] || 0;
  const actualTotal  = budgetActual?.[0]?.["Total Actual"] || 0;

  const chartPairs = [
    {
      barTitle: `${year} Revenue Performance by Economic`,
      pieTitle: `${year} Revenue Composition by Economic`,
      barData:  newRev,
      pieData:  pieChartData,
      uvKey:    "Quarterly Budget",
      pvKey:    "Actual for Quarter",
    },
    {
      barTitle: `${year} Expenditure Performance by Economic`,
      pieTitle: `${year} Expenditure Composition by Economic`,
      barData:  data2(expByEconomic),
      pieData:  data2(expByEconomic, "pie"),
      uvKey:    "Quarterly Budget",
      pvKey:    "Actual for Quarter",
    },
    {
      barTitle: `${year} Expenditure Performance by Main Sector`,
      pieTitle: `${year} Expenditure Composition by Main Sector`,
      barData:  data3(mainSector),
      pieData:  data3(mainSector, "pie"),
      uvKey:    "Quarterly Budget",
      pvKey:    "Actual for Quarter",
    },
    {
      barTitle: `${year} Expenditure Performance by Function`,
      pieTitle: `${year} Expenditure Composition by Function`,
      barData:  data4(expByFunc),
      pieData:  data4(expByFunc, "pie"),
      uvKey:    "Quarterly Budget",
      pvKey:    "Actual for Quarter",
    },
  ];

  const gap = isMobile ? 10 : 14;

  return (
    <div style={{ background: "#f5f7fa", minHeight: "100vh", padding: isMobile ? 10 : 20 }}>

      {/* ── Expand modal ── */}
      <Modal size="lg" show={modalOpen} onHide={() => setModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 14 }}>{selectedChart?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflowX: "auto", padding: "12px 16px" }}>
          {selectedChart?.chart}
        </Modal.Body>
      </Modal>

      {/* ── Page header + filters ── */}
      <div style={{
        display: "flex", flexWrap: "wrap", justifyContent: "space-between",
        alignItems: "flex-start", gap: 12, marginBottom: 16,
      }}>
        <div>
          <h4 style={{ fontWeight: 700, color: "#212529", margin: 0, fontSize: isMobile ? 18 : 22 }}>
            Dashboard Overview
          </h4>
          <p style={{ color: "#6c757d", margin: 0, fontSize: 12 }}>
            {year} Financial Summary
          </p>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 8 }}>
          <div>
            <label style={{ fontSize: 11, color: "#6c757d", display: "block", marginBottom: 3 }}>Period</label>
            <select
              className="form-select form-select-sm"
              name="quarter"
              value={selectQuarter.quarter}
              onChange={handleChange}
              style={{ minWidth: 90, borderRadius: 8, fontSize: 13 }}
            >
              <option value="Q1">Q1</option>
              <option value="Q2">Q2</option>
              <option value="Q3">Q3</option>
              <option value="Q4">Q4</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          {selectQuarter.quarter === "custom" && (
            <>
              {["from", "to"].map((field) => (
                <div key={field}>
                  <label style={{ fontSize: 11, color: "#6c757d", display: "block", marginBottom: 3 }}>
                    {field === "from" ? "From" : "To"}
                  </label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    name={field}
                    value={selectQuarter[field]}
                    onChange={handleChange}
                    style={{ borderRadius: 8, fontSize: 13 }}
                  />
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
        gap,
        marginBottom: gap + 2,
      }}>
        <StatCard label="Total Budget"      value={budgetTotal}  color="#0d6efd" icon="📋" subtitle={`${year} Fiscal Year`} />
        <StatCard label="Total Revenue"     value={revenueTotal} color="#2e7d32" icon="↑"  subtitle="Incoming" />
        <StatCard label="Total Expenditure" value={expenseTotal} color="#c62828" icon="↓"  subtitle="Outgoing" />
        <StatCard label="Actual Spend"      value={actualTotal}  color="#6f42c1" icon="⚡" subtitle="Budget utilized" />
      </div>

      {/* ── Aggregate perf + Flow of Funds ── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "7fr 5fr",
        gap,
        marginBottom: gap,
      }}>
        <ChartCard
          title={`${year} Aggregate Performance`}
          onExpand={() => openModal(
            `${year} Aggregate Performance`,
            <CustomBarChart data={budgetActual} height={360} uvDataKey="Total Budget" pvDataKey="Total Actual" />
          )}
        >
          <CustomBarChart data={budgetActual} height={260} uvDataKey="Total Budget" pvDataKey="Total Actual" />
        </ChartCard>

        <FlowCard
          revenueTotal={revenueTotal}
          expenseTotal={expenseTotal}
          quarter={selectQuarter.quarter}
          year={year}
        />
      </div>

      {/* ── Chart pairs ── */}
      {chartPairs.map((pair, idx) => (
        <div
          key={idx}
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "7fr 5fr",
            gap,
            marginBottom: gap,
          }}
        >
          <ChartCard
            title={pair.barTitle}
            onExpand={() => openModal(
              pair.barTitle,
              <CustomBarChart data={pair.barData} height={360} uvDataKey={pair.uvKey} pvDataKey={pair.pvKey} />
            )}
          >
            <CustomBarChart data={pair.barData} height={240} uvDataKey={pair.uvKey} pvDataKey={pair.pvKey} />
          </ChartCard>

          <ChartCard
            title={pair.pieTitle}
            onExpand={() => openModal(
              pair.pieTitle,
              <PieChartComponent data={pair.pieData} height={360} />
            )}
          >
            <PieChartComponent data={pair.pieData} height={240} />
          </ChartCard>
        </div>
      ))}
    </div>
  );
}

export default NewDashboard;
